<script setup lang="ts">
import { BsError, BsSelect } from '@afo/bsf-core'
import { useDistricts } from '@/domains/partners/multisport/composables/useDistricts'
import { useMultisportPartnersStore } from '@/domains/partners/multisport/store/useMultiSportPartnersStore'
import { useWorkedWithBs } from '~/domains/partners/multisport/const/WorkedWithBS.const'
import TextInput from '@/components/Inputs/TextInput/TextInput.vue'

const { t } = useI18n()
const { form, $v } = useMultisportPartnersStore()
const districts = useDistricts()
const { workedWithBs } = useWorkedWithBs()
const { isDesktop } = useBSBreakpoints()

</script>

<template>
  <div class="stepTwo">
    <h1 class="stepTwo__title">
      {{ $t('partners.multisport.steps.step2.subTitle') }}
    </h1>

    <div class="stepTwo__form">
      <div class="stepTwo__country">
        <div :class="isDesktop && 'stepTwo__half'">
          <BsError :message="$v.district.$errors[0]?.$message">
            <BsSelect
              v-model="form.district"
              :label="`${t('form.district.name')} *`"
              :error="!!$v.district.$errors[0]?.$message"
              :options="districts"
            />
          </BsError>
        </div>
      </div>

      <div>
        <TextInput
          id="nipNumber"
          v-model="form.nipNumber"
          name="nipNumber"
          :error-message="$v.nipNumber.$errors[0]?.$message"
          :error="!!$v.nipNumber.$errors[0]?.$message"
          :disabled="false"
          :required="true"
          :label="`${t('form.nip')} *`"
          @blur="$v.nipNumber.$touch()"
        />
      </div>
      
      <div>
        <TextInput
          id="companyName"
          v-model="form.companyName"
          :label="`${t('form.company')} *`"
          :error-message="$v.companyName.$errors[0]?.$message"
          :error="!!$v.companyName.$errors[0]?.$message"
          :required="true"
          @blur="$v.companyName.$touch()"
        />
      </div>

      <div class="stepTwo__street">
        <div class="stepTwo__half">
          <TextInput
            id="street"
            v-model="form.street"
            :label="`${t('form.street')} *`"
            :error-message="$v.street.$errors[0]?.$message"
            :error="!!$v.street.$errors[0]?.$message"
            :required="true"
            @blur="$v.street.$touch()"
          />
        </div>

        <div
          class="stepTwo__buildingNumber"
          :class="isDesktop ? 'stepTwo__twoThirds' : 'stepTwo__half'"
        >
          <div :class="isDesktop && 'stepTwo__half'">
            <TextInput
              id="buildingNumber"
              v-model="form.buildingNumber"
              :label="`${t('form.buildingNumber')} *`"
              :error-message="$v.buildingNumber.$errors[0]?.$message"
              :error="!!$v.buildingNumber.$errors[0]?.$message"
              :required="true"
              @blur="$v.buildingNumber.$touch()"
            />
          </div>

          <div
            v-if="isDesktop"
            class="stepTwo__half"
          >
            <TextInput
              id="apartmentNumber"
              v-model="form.apartmentNumber"
              name="apartmentNumber"
              :label="`${t('form.apartmentNumber')}`"
              :error-message="$v.apartmentNumber.$errors[0]?.$message"
              :error="!!$v.apartmentNumber.$errors[0]?.$message"
              :required="false"
              @blur="$v.apartmentNumber.$touch()"
            />
          </div>
        </div>
      </div>

      <div v-if="!isDesktop">
        <TextInput
          id="apartmentNumber"
          v-model="form.apartmentNumber"
          name="apartmentNumber"
          :label="`${t('form.apartmentNumber')}`"
          :error-message="$v.apartmentNumber.$errors[0]?.$message"
          :error="!!$v.apartmentNumber.$errors[0]?.$message"
          :required="false"
          @blur="$v.apartmentNumber.$touch()"
        />
      </div>

      <div class="stepTwo__city">
        <div :class="isDesktop && 'stepTwo__half'">
          <TextInput
            id="city"
            v-model="form.city"
            name="city"
            :label="`${t('form.city')} *`"
            :error-message="$v.city.$errors[0]?.$message"
            :error="!!$v.city.$errors[0]?.$message"
            :required="true"
            @blur="$v.city.$touch()"
          />
        </div>

        <div :class="isDesktop && 'stepTwo__half'">
          <TextInput
            id="zipCode"
            ref="zipCodeInput"
            v-model="form.zipCode"
            name="zipCode"
            :error-message="$v.zipCode.$errors[0]?.$message"
            :error="!!$v.zipCode.$errors[0]?.$message"
            :disabled="false"
            :required="true"
            :label="$t('form.zipCode')"
            @blur="$v.zipCode.$touch()"
          />
        </div>
      </div>

      <div class="stepTwo__radio">
        <p class="stepTwo__radioText">
          {{ $t('partners.multisport.steps.step2.workedWithBs') }}
        </p>

        <RadioButtonController
          v-model="form.workedWithBS"
          :radio-controls="workedWithBs"
          :error="!!$v.workedWithBS.$errors[0]?.$message"
          @update="$v.workedWithBS.$validate()"
        />
      </div>

      <div class="stepTwo__requiredFields">
        {{ $t('partners.multisport.requiredFields') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.stepTwo {
  &__title {
    @include h11;
    margin-bottom: 3.2rem;
    color: $text-500;
    text-align: center;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
    padding-bottom: 1rem;
  }

  &__country,
  &__city,
  &__buildingNumber {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    gap: 1.6rem;
  }

  &__street {
    display: flex;
    flex-grow: 1;
    flex-direction: row;
    gap: 1.6rem;
    width: 100%;

    :deep(.bs-error) {
      flex: 1 1 0;
      min-width: 0;
    }

    :deep(.bs-input),
    :deep(.bs-select) {
      width: 100%;
    }
  }

  &__half {
    width: 50%;
  }

  &__third {
    width: 33.33%;
  }

  &__twoThirds {
    width: 66%;
  }

  &__radio {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
    margin: 1.6rem 0;
  }

  &__radioText {
    @include h11;
    color: $text-500;
    text-align: center;
  }

  &__requiredFields {
    @include body-4;
    padding: 3.2rem 0;
    color: $text-200;
    text-align: center;
  }

  @include atM {
    &__country,
    &__city,
    &__buildingNumber {
      flex-direction: row;
    }

    &__radioText {
      text-align: left;
    }

    &__requiredFields {
      text-align: left;
    }
  }
}
</style>
